import React, { useCallback } from "react";
import { Dropdown, Tooltip, message } from "antd";
import type { MenuProps } from "antd";
import classNames from "classnames";

type Props = {
  tooltip: string;
  icon: React.ReactNode;
  menuItems?: MenuProps["items"];
  onClick: () => void | Promise<void>;
  onMenuClick?: MenuProps["onClick"];
  disabled?: boolean;
  className?: string;
};

function errToText(e: any) {
  if (!e) return "Unknown error";
  if (typeof e === "string") return e;
  if (e.reason) return String(e.reason);
  if (e.type && e.reason) return `${e.type}: ${e.reason}`;
  if (e.message) return String(e.message);
  try {
    return JSON.stringify(e);
  } catch {
    return String(e);
  }
}

export default function IconDropdownButton({
  tooltip,
  icon,
  menuItems,
  onClick,
  onMenuClick,
  disabled,
  className,
}: Props) {
  const hasMenu = !!menuItems?.length;

  const safeOnClick = useCallback(() => {
    try {
      const ret = onClick?.();
      // IMPORTANT: swallow any async rejections so CRA overlay doesn't show [object Object]
      if (ret && typeof (ret as any).then === "function") {
        (ret as Promise<void>).catch((e) => {
          console.warn("IconDropdownButton onClick failed", e);
          message.error(errToText(e));
        });
      }
    } catch (e) {
      console.warn("IconDropdownButton onClick threw", e);
      message.error(errToText(e));
    }
  }, [onClick]);

  const safeMenuClick: MenuProps["onClick"] = useCallback(
    (info: any) => {
      try {
        const ret = onMenuClick?.(info);
        if (ret && typeof (ret as any).then === "function") {
          (ret as Promise<any>).catch((e) => {
            console.warn("IconDropdownButton menu click failed", e);
            message.error(errToText(e));
          });
        }
      } catch (e) {
        console.warn("IconDropdownButton menu click threw", e);
        message.error(errToText(e));
      }
    },
    [onMenuClick]
  );

  return (
    <Tooltip title={tooltip}>
      <div className={classNames(className)}>
        {hasMenu ? (
          <Dropdown.Button
            className="vc-dropdown-button"
            size="large"
            placement="topRight"
            trigger={["click"]}
            menu={{ items: menuItems, onClick: safeMenuClick }}
            onClick={safeOnClick}
            disabled={disabled}
          >
            {icon}
          </Dropdown.Button>
        ) : (
          <button
            className="vc-button"
            onClick={safeOnClick}
            disabled={disabled}
            title={tooltip}
            type="button"
          >
            {icon}
          </button>
        )}
      </div>
    </Tooltip>
  );
}
